import React, { useState } from 'react';

// ----- Types -----

interface Conversation {
  id: string;
  otherUser: {
    id: string;
    displayName: string;
    photoUrl: string | null;
  };
  lastMessage: string;
  lastMessageAt: string;
  unread: boolean;
}

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  sentAt: string;
}

// ----- Mock data -----
const MOCK_CONVERSATIONS: Conversation[] = [];

// ----- Constants -----
const MAX_MESSAGE_LENGTH = 2000;

// ----- Main Page -----

/**
 * DirectMessagesPage — conversation list on the left, message thread on the right.
 * Compose input at bottom with character counter.
 */
export function DirectMessagesPage(): React.JSX.Element {
  const [conversations] = useState<Conversation[]>(MOCK_CONVERSATIONS);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [composeContent, setComposeContent] = useState('');

  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  function handleSelectConversation(conversationId: string) {
    setSelectedConversationId(conversationId);
    // TODO: call GET /messages/:conversationId
    setMessages([]);
  }

  function handleSendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!composeContent.trim() || composeContent.length > MAX_MESSAGE_LENGTH) return;
    // TODO: call POST /messages
    setComposeContent('');
  }

  return (
    <div className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary">Messages</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Private conversations with your connections.
          </p>
        </div>

        <div className="grid h-[calc(100vh-16rem)] grid-cols-1 overflow-hidden rounded-lg border border-border bg-surface-raised md:grid-cols-[300px_1fr]">
          {/* Left: Conversation list */}
          <aside className="flex flex-col border-r border-border">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-text-primary">Conversations</h2>
            </div>

            {conversations.length === 0 ? (
              <div className="flex flex-1 items-center justify-center p-4">
                <p className="text-center text-sm text-text-muted">
                  No conversations yet. Connect with people to start messaging.
                </p>
              </div>
            ) : (
              <ul className="flex-1 overflow-y-auto">
                {conversations.map((conv) => (
                  <li key={conv.id}>
                    <button
                      onClick={() => handleSelectConversation(conv.id)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-surface-muted ${
                        selectedConversationId === conv.id ? 'bg-surface-muted' : ''
                      }`}
                    >
                      {conv.otherUser.photoUrl ? (
                        <img
                          src={conv.otherUser.photoUrl}
                          alt=""
                          className="h-8 w-8 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-muted">
                          <UserIcon />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <p
                            className={`truncate text-sm ${
                              conv.unread ? 'font-semibold text-text-primary' : 'text-text-primary'
                            }`}
                          >
                            {conv.otherUser.displayName}
                          </p>
                          <span className="ml-2 shrink-0 text-[10px] text-text-muted">
                            {conv.lastMessageAt}
                          </span>
                        </div>
                        <p className="truncate text-xs text-text-muted">{conv.lastMessage}</p>
                      </div>
                      {conv.unread && (
                        <span className="h-2 w-2 shrink-0 rounded-full bg-byron" aria-label="Unread" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Right: Message thread */}
          <div className="flex flex-col">
            {selectedConversation ? (
              <>
                {/* Thread header */}
                <div className="border-b border-border px-4 py-3">
                  <p className="text-sm font-semibold text-text-primary">
                    {selectedConversation.otherUser.displayName}
                  </p>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4">
                  {messages.length === 0 ? (
                    <p className="text-center text-sm text-text-muted">
                      No messages yet. Say hello!
                    </p>
                  ) : (
                    <MessageThread messages={messages} />
                  )}
                </div>

                {/* Compose */}
                <form
                  onSubmit={handleSendMessage}
                  className="flex items-end gap-2 border-t border-border p-4"
                >
                  <div className="flex-1">
                    <textarea
                      value={composeContent}
                      onChange={(e) => setComposeContent(e.target.value)}
                      placeholder="Type a message…"
                      rows={2}
                      maxLength={MAX_MESSAGE_LENGTH}
                      className="w-full resize-none rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-byron focus:outline-none focus:ring-1 focus:ring-byron"
                      aria-label="Type a message"
                    />
                    <span className="mt-1 block text-right text-[10px] text-text-muted">
                      {composeContent.length}/{MAX_MESSAGE_LENGTH}
                    </span>
                  </div>
                  <button
                    type="submit"
                    disabled={!composeContent.trim() || composeContent.length > MAX_MESSAGE_LENGTH}
                    className="rounded bg-byron px-4 py-2 text-sm font-medium text-white hover:bg-byron-accent disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Send
                  </button>
                </form>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <p className="text-sm text-text-muted">Select a conversation to start messaging.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Message Thread -----

function MessageThread({ messages }: { messages: Message[] }) {
  // Group consecutive messages from same sender
  const groups: Message[][] = [];
  for (const msg of messages) {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup[0].senderId === msg.senderId) {
      lastGroup.push(msg);
    } else {
      groups.push([msg]);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group[0].id}>
          <p className="mb-1 text-xs font-medium text-text-secondary">{group[0].senderName}</p>
          <div className="flex flex-col gap-1">
            {group.map((msg) => (
              <div key={msg.id} className="flex items-end gap-2">
                <p className="rounded-lg bg-surface-muted px-3 py-2 text-sm text-text-primary">
                  {msg.content}
                </p>
                <span className="shrink-0 text-[10px] text-text-muted">{msg.sentAt}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ----- Icons -----

function UserIcon() {
  return (
    <svg className="h-4 w-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </svg>
  );
}
